import * as React from "react";
import { createPortal } from "react-dom";

// `undefined` = no provider on this page (render actions in place);
// `null` = a provider exists but its slot node hasn't attached yet (first
// commit only — render nothing so the actions never flash in the tab body).
const PageHeaderActionsContext = React.createContext<HTMLElement | null | undefined>(undefined);

/**
 * Provides the DOM node a page reserves in its title row for the active
 * view's actions. Pages render `<div ref={setNode} />` where the actions
 * belong and pass the node here.
 */
export function PageHeaderActionsProvider({
  target,
  children,
}: {
  target: HTMLElement | null;
  children: React.ReactNode;
}) {
  return (
    <PageHeaderActionsContext.Provider value={target}>{children}</PageHeaderActionsContext.Provider>
  );
}

/**
 * Renders its children into the host page's title-row action slot. Falls back
 * to rendering them in place when no slot is mounted, so the same view works
 * on pages that keep their actions in their own toolbar (e.g. the folder view
 * on /projects/:id).
 */
export function PageHeaderActions({ children }: { children: React.ReactNode }) {
  const target = React.useContext(PageHeaderActionsContext);
  if (target === undefined) return <>{children}</>;
  return target ? createPortal(children, target) : null;
}
